import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { API_ENDPOINT } from "../../utils/constants";
import { getBillingOptions, durationLabel } from "../../utils/pricingPlanUtils";
import { getHeader } from "../../utils/util";
import { ZOHO_API_KEY } from "./PaymentWidget";
import BackBtn from "../../widgets/BackBtn";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(value);

const PlanCheckout = () => {
  const { planId, cycleKey } = useParams();
  const navigate = useNavigate();
  const [selection, setSelection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    let active = true;
    const loadPlan = async () => {
      try {
        const response = await fetch(`${API_ENDPOINT}/root/pricing-plans`, {
          method: "GET",
          ...getHeader(),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not load plans");
        const plan = (data.plans || []).find(
          (item) => item._id === planId && item.active !== false
        );
        const cycle = plan && getBillingOptions(plan).find((item) => item.key === cycleKey);
        if (!cycle) throw new Error("This billing option is no longer available.");
        if (active) setSelection({ plan, cycle });
      } catch (err) {
        if (active) setError(err.message || "Could not load plan");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadPlan();
    return () => {
      active = false;
    };
  }, [planId, cycleKey]);

  const submit = async () => {
    if (!selection || processing) return;
    setProcessing(true);
    setError("");
    let widget;
    try {
      const isFree = selection.plan.isFree === true;
      const response = await fetch(
        `${API_ENDPOINT}/payment/${isFree ? "activate-free-plan" : "create-plan-payment-session"}`,
        {
          method: "POST",
          ...getHeader(),
          body: JSON.stringify({ planId, cycleKey }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Could not start checkout");

      if (isFree) {
        setComplete(true);
        return;
      }

      // A plan can change while this page is open. Require a fresh review if it did.
      if (
        data.pricing.baseAmount !== Number(selection.cycle.price) ||
        data.pricing.months !== selection.cycle.months
      ) {
        throw new Error("This plan has changed. Return to pricing plans and review it again.");
      }

      widget = new window.ZPayments({
        account_id: data.paymentAccountId,
        domain: "IN",
        otherOptions: { api_key: ZOHO_API_KEY },
      });
      const session = data.payments_session;
      const account = data.accountInfo;
      const payment = await widget.requestPaymentMethod({
        amount: data.pricing.totalAmount.toFixed(2),
        currency_code: "INR",
        payments_session_id: session.payments_session_id,
        currency_symbol: "₹",
        business: account.id,
        description: `${selection.plan.name} for ${durationLabel(selection.cycle.months)}`,
        invoice_number: session.invoice_number,
        reference_number: `${account.id}-${Date.now()}`.slice(0, 50),
        address: {
          name: account.verifiedName,
          email: account.email,
          phone: account.phoneId,
        },
      });
      if (payment?.payment_id) setComplete(true);
    } catch (err) {
      if (err.code !== "widget_closed") {
        setError(err.message || "Payment could not be started");
      }
    } finally {
      if (widget) await widget.close();
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  const isFree = selection?.plan.isFree === true;
  const price = Number(selection?.cycle.price || 0);
  const gst = Math.round(price * 18) / 100;

  return (
    <Container maxWidth="sm" sx={{ mt: 3, pb: 5 }}>
      <BackBtn />
      <Card sx={{ p: { xs: 3, sm: 4 }, mt: 2 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {isFree ? "Activate free plan" : "Plan checkout"}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {complete ? (
          <Stack spacing={2}>
            <Alert severity="success">
              {isFree
                ? "Your free plan is active."
                : "Payment submitted. Your plan will activate when payment confirmation arrives."}
            </Alert>
            <Button variant="contained" onClick={() => navigate("/home")}>
              Go to dashboard
            </Button>
          </Stack>
        ) : selection ? (
          <Stack spacing={2}>
            <Typography variant="h6">{selection.plan.name}</Typography>
            <Typography color="text.secondary">
              {selection.cycle.label || durationLabel(selection.cycle.months)}
            </Typography>
            <Divider />
            <Typography>Plan price: {isFree ? "Free" : formatMoney(price)}</Typography>
            {!isFree && (
              <>
                <Typography>GST (18%): {formatMoney(gst)}</Typography>
                <Typography fontWeight={700}>Total: {formatMoney(price + gst)}</Typography>
              </>
            )}
            <Button variant="contained" disabled={processing} onClick={submit}>
              {processing ? "Processing..." : isFree ? "Activate plan" : "Pay now"}
            </Button>
          </Stack>
        ) : null}
      </Card>
    </Container>
  );
};

export default PlanCheckout;
